import { Component, OnDestroy, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  occupationsToFormValue,
  PurposeControlType,
  RoadOccupationForm,
  WorkPurposeControlType
} from './forms/road-occupation.form';
import { RoadOccupationFormComponent } from './components/road-occupation-form/road-occupation-form.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { ApplicationAppEntityApplicationRead, RoadOccupationAppEntityApplicationRead } from '../../../../../openapi';
import { selectApplication, selectLoading, selectPending } from '../../../../../store/selectors/application.selectors';
import { loadRoadOccupationTypes, updateOccupations } from '../../../../../store/actions/application.actions';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { ServiceListItem } from '../../../../../services/service.common.model';
import { selectServiceByUrl } from '../../../../../store/selectors/app.selectors';

export interface OccupationDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  addDecisionControls?: boolean;
  hideOccupationTypeControl?: boolean;
  hidePurposeControl?: boolean;
  hideCityControl?: boolean;
  addWorkPurposeControl?: boolean;
  workPurposeControlType?: 'select' | 'textarea';
  purposeControlType?: 'input' | 'textarea';
  addTypeOfFailureControl?: boolean;
  addSurfaceViolationControl?: boolean;
}

@Component({
  selector: 'app-occupation-data-section',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    RoadOccupationFormComponent,
    ReactiveFormsModule,
    StagesComponent,
    FormsModule
  ],
  providers: [RoadOccupationForm],
  templateUrl: './occupation-data-section.component.html'
})
export class OccupationDataSectionComponent implements OnInit, OnDestroy {

  public applicationId: string | undefined = this.route.snapshot.params['appId'];

  public successUrl: string | undefined = this.route.snapshot.data['successUrl'];

  public cancelUrl: string | undefined = this.route.snapshot.data['cancelUrl'];

  public stages: string[] = this.route.snapshot.data['stages'];

  public workPurposeControlType: WorkPurposeControlType = this.route.snapshot.data['workPurposeControlType'] || 'select';

  public purposeControlType: PurposeControlType = this.route.snapshot.data['purposeControlType'] || 'input';

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  private fetchRoadOccupation: Subscription = toObservable(this.application).subscribe(app => {
    const occupations: Array<RoadOccupationAppEntityApplicationRead> = app?.roadOccupations || [];
    const occupation = occupationsToFormValue(occupations).at(0) || null;
    if (occupation) {
      this.form.patchFormValues(occupation);
    }
  });

  public serviceType: Signal<ServiceListItem | null> = this.store$.selectSignal(selectServiceByUrl(this.route?.parent?.snapshot?.data['contentUrl']));

  constructor(
    public form: RoadOccupationForm,
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store
  ) {
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public submitAndGoNext(): void {
    if (this.form.valid) {
      this.store$.dispatch(updateOccupations({
        application: this.application(),
        occupations: [this.form.values],
        successFnc: (applicationId) => {
          this.router.navigate(
            [this.successUrl],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      }));
    } else {
      this.form.markAllAsTouched();
    }
  }

  public ngOnInit(): void {
    this.store$.dispatch(loadRoadOccupationTypes());
    if (this.route.snapshot.data['addDecisionControls']) {
      this.form.createDecisionControl();
    }
    if (this.route.snapshot.data['hideOccupationTypeControl']) {
      this.form.removeControl('occupationType');
    }
    if (this.route.snapshot.data['hidePurposeControl']) {
      this.form.removeControl('purpose');
    }
    if (this.route.snapshot.data['addTypeOfFailureControl']) {
      this.form.createTypeOfFailureControl();
    }
    if (this.route.snapshot.data['addSurfaceViolationControl']) {
      this.form.createSurfaceViolationControl();
    }
    if (this.route.snapshot.data['addWorkPurposeControl']) {
      this.form.createWorkPurposeControl();
    }
    if (this.route.snapshot.data['hideCityControl']) {
      this.form.removeControl('city');
    }
  }

  public ngOnDestroy(): void {
    this.fetchRoadOccupation.unsubscribe();
  }

}
